import { getMetrikaCounterIdFromEnv } from "./metrikaCounterId";
import { getOrCreateSiteMetrikaClientId, isValidMetrikaNumericCid, readYmUidCookie, SITE_METRIKA_CLIENT_ID_KEY, } from "./siteAnalyticsStorage";
const METRIKA_INIT = {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: "dataLayer",
    accurateTrackBounce: true,
    trackLinks: true,
};
let resolveCidPromise = null;
// Реальный ym ClientID (из getClientID), полученный на этой загрузке. Только
// его кэшируем навсегда — fallback-значения не «замораживаем», чтобы успеть
// заменить их реальным id, как только счётчик поднимется.
let resolvedRealCid = null;
function getProxyUrlFromEnv() {
    // Базовый URL прокси Метрики (свой домен). Подставляется webpack
    // DefinePlugin строковым литералом — см. metrikaCounterId.ts.
    const raw = process.env.REACT_APP_YM_PROXY_URL;
    if (!raw || typeof raw !== "string") {
        return null;
    }
    const t = raw.trim().replace(/\/+$/, "");
    return t || null;
}
/**
 * Очередь вызовов ym до загрузки tag.js (совместимо с официальным сниппетом).
 */
function installMetrikaStub() {
    if (typeof window === "undefined") {
        return;
    }
    if (window.ym) {
        return;
    }
    new Function("w", "w.ym=w.ym||function(){(w.ym.a=w.ym.a||[]).push(arguments)};w.ym.l=1*new Date()")(window);
}
function appendScript(src) {
    return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.async = true;
        s.src = src;
        s.onload = () => resolve();
        s.onerror = () => {
            s.remove();
            reject(new Error(`script load failed: ${src}`));
        };
        document.head.appendChild(s);
    });
}
/**
 * tag.js уже подключён синхронным сниппетом из <head>. Тогда повторно
 * грузить скрипт не нужно — иначе будет дубликат тега.
 */
function isMetrikaTagScriptPresent() {
    if (typeof document === "undefined") {
        return false;
    }
    return !!document.querySelector('script[src*="/metrika/tag.js"]');
}
/**
 * Грузит tag.js с прокси-домена. Обычно синхронный сниппет в <head> уже
 * вставил его — тогда выходим сразу: нужен лишь стаб очереди ym (на случай,
 * если скрипт ещё грузится), а getClientID отработает, как только tag.js
 * догрузится.
 */
async function loadMetrikaTag(proxyUrl, counterId) {
    installMetrikaStub();
    if (isMetrikaTagScriptPresent()) {
        return;
    }
    const q = encodeURIComponent(counterId);
    await appendScript(`${proxyUrl}/metrika/tag.js?id=${q}`);
}
function initMetrikaOnce(counterId) {
    if (!window.ym) {
        return;
    }
    if (window.__VIRTUOZ_METRIKA_INIT_COUNTER === counterId) {
        return;
    }
    window.ym(counterId, "init", { ...METRIKA_INIT });
    window.__VIRTUOZ_METRIKA_INIT_COUNTER = counterId;
}
function readYmClientId(counterId, timeoutMs) {
    return new Promise((resolve) => {
        if (!window.ym) {
            resolve({ cid: null, reason: "window.ym отсутствует (tag.js не загрузился)" });
            return;
        }
        const done = (r) => {
            clearTimeout(timer);
            resolve(r);
        };
        const timer = window.setTimeout(() => done({ cid: null, reason: `таймаут getClientID (${timeoutMs}ms)` }), timeoutMs);
        try {
            window.ym(counterId, "getClientID", (cid) => {
                const t = typeof cid === "string" ? cid.trim() : "";
                if (t && isValidMetrikaNumericCid(t)) {
                    done({ cid: t, reason: null });
                }
                else {
                    done({
                        cid: null,
                        reason: `getClientID вернул невалидный id: ${JSON.stringify(cid)}`,
                    });
                }
            });
        }
        catch (e) {
            done({ cid: null, reason: `getClientID бросил исключение: ${String(e)}` });
        }
    });
}
function logResolvedCid(cid, source) {
    // eslint-disable-next-line no-console
    console.info(`[metrika] ycid=${cid} (источник: ${source})`);
}
function logYmCidFallbackReason(reason) {
    // eslint-disable-next-line no-console
    console.warn(`[metrika] ym getClientID не получен — ${reason}; берём fallback`);
}
function persistCid(cid) {
    try {
        localStorage.setItem(SITE_METRIKA_CLIENT_ID_KEY, cid);
    }
    catch {
        /* ignore */
    }
}
async function resolveOnce() {
    const counterId = getMetrikaCounterIdFromEnv();
    const proxyUrl = getProxyUrlFromEnv();
    // 1. Основной путь — реальный ym ClientID из счётчика (getClientID).
    if (counterId === null) {
        logYmCidFallbackReason("не задан REACT_APP_YM_COUNTER_ID");
    }
    else if (proxyUrl === null) {
        logYmCidFallbackReason("не задан REACT_APP_YM_PROXY_URL");
    }
    else {
        try {
            await loadMetrikaTag(proxyUrl, String(counterId));
            initMetrikaOnce(counterId);
            await new Promise((r) => {
                window.setTimeout(r, 80);
            });
            const { cid: ymCid, reason } = await readYmClientId(counterId, 5000);
            if (ymCid) {
                persistCid(ymCid);
                resolvedRealCid = ymCid;
                logResolvedCid(ymCid, "ym getClientID");
                return ymCid;
            }
            logYmCidFallbackReason(reason || "getClientID не вернул id");
        }
        catch (e) {
            logYmCidFallbackReason(`tag.js не загрузился с прокси ${proxyUrl}: ${String(e)}`);
        }
    }
    // 2. Счётчик не поднялся — берём ClientID из cookie `_ym_uid` (его ставит
    //    сам tag.js на прошлых заходах; значение совпадает с ym getClientID).
    const ymUid = readYmUidCookie();
    if (ymUid) {
        persistCid(ymUid);
        logResolvedCid(ymUid, "cookie _ym_uid");
        return ymUid;
    }
    // 3. Совсем ничего нет — стабильный синтетический ID (20 цифр: 10 —
    //    таймстамп первого захода, 10 — random), сразу persist-ится.
    const fallbackCid = getOrCreateSiteMetrikaClientId();
    logResolvedCid(fallbackCid, "сохранённый/синтетический (нет ym и cookie)");
    return fallbackCid;
}
/**
 * ClientID (ycid) для бэка — один на загрузку страницы. Цепочка фолбэков:
 * ym getClientID → cookie `_ym_uid` → синтетический 20-значный id. Значение
 * есть всегда (поле ycid больше не «пропадает»).
 */
export function resolveSiteMetrikaClientId() {
    if (resolvedRealCid) {
        return Promise.resolve(resolvedRealCid);
    }
    if (!resolveCidPromise) {
        resolveCidPromise = resolveOnce().then((cid) => {
            // Кэшируем промис навсегда только если получили РЕАЛЬНЫЙ ym ClientID.
            // На fallback-значении сбрасываем кэш: следующий вызов снова попробует
            // поднять счётчик, а пока вернёт стабильный fallback (cookie/persisted).
            if (!resolvedRealCid) {
                resolveCidPromise = null;
            }
            return cid;
        });
    }
    return resolveCidPromise;
}
